import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import _ from "lodash";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Card, CardBody, Dropdown, DropdownItem, DropdownMenu, Input, Table } from "reactstrap";
import { ServiceThreadSpinner, SortHeader } from ".";
import { api, constants, helpers, reportHelpers } from "../utils";

export default function InventoryDashBoardCurrentInventoryGrid({isLotTracked = true, ...props}) {
   const {
      selectedItem,
      currentInvSortField,
      currentInvSortDirection
   } = props.params;
   const [counts, setCounts] = useState({ ids: [], qtyList: [] });
   const [data, setData] = useState([]);
   const [editCopy, setEditCopy] = useState(null);
   const [editIndex, setEditIndex] = useState(null);
   const [isLoading, setIsLoading] = useState({ cycleCount: false });
   const [isWhEdit, setIsWhEdit] = useState(false);
   const [loaded, setLoaded] = useState(false);
   const [params, setParams] = useState({});
   const [showCycle, setShowCycle] = useState(false);
   const [showEdits, setShowEdits] = useState(false);
   const [total, setTotal] = useState({
      totalOnHand: 0,
      totalAllocated: 0,
      totalAvailable: 0,
      totalOnOrder: 0
   });
   const [working, setWorking] = useState(false);
   const payload = {
      ItemNumber: selectedItem?.value,
      CurrentInvSortField: currentInvSortField,
      CurrentInvSortDirection: currentInvSortDirection
   };

   useEffect(() => {
      // Can update cycle count quantities?
      setShowCycle(!props.isLotTracked && helpers.hasRole(props.roles, [constants.ROLE_IDS.Admin, constants.ROLE_IDS.PowerUser, constants.ROLE_IDS.EngineeringEdit, constants.ROLE_IDS.WarehouseEdit, constants.ROLE_IDS.InventoryEdit]));

      // Edit allowances? - Whse only is restricted, so if user has other edit roles make it false.
      let allow = helpers.hasRole(props.roles, [constants.ROLE_IDS.Admin, constants.ROLE_IDS.PowerUser, constants.ROLE_IDS.PurchasingEdit]);
      let whsAllow = helpers.hasRole(props.roles, [constants.ROLE_IDS.WarehouseEdit]);
      setShowEdits(allow || whsAllow);
      setIsWhEdit(!allow && whsAllow);
   }, [props])

   useEffect(() => {
      const cancelToken = axios.CancelToken;
      const source = cancelToken.source();
      if (props.params.selectedItem?.value !== params.selectedItem?.value) {
         setParams(props.params)
         setLoaded(false); //for the spinner
      }
      api
         .postS(`Inventory/CurrentInventory`, payload, source.token)
         .then(response => {
            if (response.data.success) {
               const dataResponse = response.data.itemGrid.currentInventory;
               setData(dataResponse);
               setTotal({
                  totalOnHand: dataResponse.reduce((total, currentItem) => (total = total + (currentItem.qtyOnHand)), 0),
                  totalAllocated: dataResponse.reduce((total, currentItem) => (total = total + (currentItem.qtyAllocated)), 0),
                  totalAvailable: dataResponse.reduce((total, currentItem) => (total = total + (currentItem.qtyAvailable)), 0),
                  totalOnOrder: dataResponse.reduce((total, currentItem) => (total = total + ((currentItem.qtyOnOrder + currentItem.qtyBackOrdered))), 0)
               });
               setCounts({ ids: [], qtyList: [] });
            } else {
               throw new Error(response);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setLoaded(true));

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.params])

   function allowEdit(index) {
      setEditIndex(index);
      setEditCopy(_.assign({}, data[index]));
   }

   function cancelEdit() {
      setEditIndex(null);
      setEditCopy(null);
   }

   function handleChange(field, value) {
      const copyUpdate = _.assign({}, editCopy);
      copyUpdate[field] = value;
      if (field === "bin")
         copyUpdate.binTrimmed = value.trim();
      setEditCopy(copyUpdate);
   }

   function onChange(idx, value) {
      value = (value === 0 || typeof value === "number")
         ? parseFloat(value)
         : null;
      const copy = _.cloneDeep(counts);
      copy.qtyList[idx].cycleCountQty = value;
      setCounts(copy);
   }

   function processQuantities() {
      let qtyListCopy = _.cloneDeep(counts.qtyList);
      qtyListCopy = _.map(Object.assign({}, qtyListCopy), (qty, idx) => ({ ...qty, refIndx: idx }));
      let payload = _.cloneDeep(qtyListCopy);
      payload = _.filter(payload, c => c.cycleCountQty === 0 || (c.cycleCountQty && c.cycleCountQty !== ""));
      _.map(payload, (count) => {
         count.cycleCountQty = parseFloat(count.cycleCountQty);
         return count;
      });
      setIsLoading({
         ...isLoading,
         cycleCount: true
      });
      api.post("Inventory/CycleCount", payload).then((response) => {
         if (response && response.data?.success) {
            toast.success(`Successfully cycled counts. Please wait a moment for the page to refresh.`);
            let countsCopy = _.cloneDeep(counts);
            _.map(countsCopy.qtyList, c => c.cycleCountQty = "");
            setCounts(countsCopy);
         }
      }).finally(() => {
         setTimeout(() => {
            setIsLoading({
               ...isLoading,
               cycleCount: false
            });
            props.onUpdateRow({ typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory, });
         }, 10000);
      });
   }

   function updateItemDesc() {
      setWorking(true);
      const payload = {
         bin: editCopy?.bin,
         fixedOrderQty: editCopy?.fixedOrderQty,
         orderPointQty: editCopy?.orderPointQty,
         itemNumber: editCopy?.itemNumber,
         siteId: editCopy?.siteId
      };
      if (payload
         && (payload.bin !== data[editIndex]?.bin
            || payload.fixedOrderQty !== data[editIndex]?.fixedOrderQty
            || payload.orderPointQty !== data[editIndex]?.orderPointQty)) {
         api
            .post(`Inventory/UpdateGPBinOrderPointAndQty`, payload)
            .then((response) => {
               if (response.data.success) {
                  props.onUpdateRow({ typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory, });
               } else {
                  if (_.isString(response?.data?.message)) {
                     toast.error(response?.data?.message);
                  }
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               cancelEdit();
            });
      } else {
         cancelEdit();
      }
      setWorking(false);
   }

   return (
      <>
         <Card className="dashboard-card">
            <CardBody>
               <div>
                  <span className="h2 me-2">Current Inventory</span>
                  {/* <span className="text-muted">Customer {customerI}</span> */}
                  {!showCycle ? null : (
                     <Button
                        onClick={() => processQuantities()}
                        className="shredButton float-end"
                        disabled={isLoading.cycleCount}
                     >
                        Process Count Quantities
                     </Button>
                  )}
               </div>
               <hr />
               <ServiceThreadSpinner on={!loaded}>
                  {loaded && data.length !== 0 ?
                     <Table size="sm" className="data-table">
                        <thead className="table-header">
                           <tr>
                              <th style={{ width: "10%" }}>
                                 <SortHeader
                                    displayName={"Site"}
                                    field={"SiteId"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "SiteId"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "SiteId",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"On Hand"}
                                    field={"QtyOnHand"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "QtyOnHand"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "QtyOnHand",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"Allocated"}
                                    field={"QtyAllocated"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "QtyAllocated"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "QtyAllocated",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"Available"}
                                    field={"QtyAvailable"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "QtyAvailable"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "QtyAvailable",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"On Order"}
                                    field={"QtyOnOrder"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "QtyOnOrder"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "QtyOnOrder",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"Order Point"}
                                    field={"OrderPointQty"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "OrderPointQty"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "OrderPointQty",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName={"Fixed Order Qty"}
                                    field={"FixedOrderQty"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "FixedOrderQty"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "FixedOrderQty",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              <th style={{ width: "12%" }}>
                                 <SortHeader
                                    displayName={"Bin Location"}
                                    field={"Bin"}
                                    sortDirection={props.sortDirection}
                                    sorted={props.sortField === "Bin"}
                                    callback={props.sortCallback}
                                    state={{
                                       currentInvSortField: "Bin",
                                       currentInvSortDirection: props.sortDirection === "asc" ? "desc" : "asc",
                                       typeOfDataSource: constants.INVENTORYDASHBOARD_TYPES.CurrentInventory,
                                    }}
                                 />
                              </th>
                              {!showCycle ? null : (
                                 <th style={{ width: "18%" }}>Cycle Count Qty</th>
                              )}
                              {showEdits ? <th></th> : null}
                              {/*based on permissions show the edit column*/}
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(data, (datum, index) => {
                              const isEditing = editIndex === index;
                              const readOnly = !isEditing;
                              if (!_.includes(counts.ids, index)) {
                                 counts.ids.push(index);
                                 counts.qtyList.push({
                                    id: index,
                                    siteId: datum.siteId,
                                    itemNumber: datum.itemNumber,
                                    qtyOnHand: datum.qtyOnHand,
                                    cycleCountQty: ""
                                 });
                              }
                              return (
                                 <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                    <td>{datum.siteId}</td>
                                    <td className="data-cell" children={Math.round(datum.qtyOnHand).toLocaleString()} />
                                    <td className="data-cell" children={Math.round(datum.qtyAllocated).toLocaleString()} />
                                    <AvailableDropDownMenu
                                       available={Math.round(datum.qtyAvailable).toLocaleString()}
                                       itemNum={datum.itemNumber}
                                       siteId={datum.siteId}
                                    />
                                    {/*<td*/}
                                    {/*   className="data-cell text-shredOrange"*/}
                                    {/*   style={{ cursor: "pointer" }}*/}
                                    {/*   children={Math.round(datum.qtyAvailable).toLocaleString()}*/}
                                    {/*   onClick={() => reportHelpers.getCaseLotInventoryReport(datum.itemNumber, datum.siteId)}*/}
                                    {/*/>*/}
                                    <td className="data-cell">
                                       <Link
                                          className="text-shredOrange"
                                          style={{ textDecoration: "none" }}
                                          to={`${constants.ENDPOINTS.itemSiteOrders}/${datum.itemNumber}/${datum.siteId}`}
                                          target="_blank">
                                          {Math.round(datum.qtyOnOrder + datum.qtyBackOrdered).toLocaleString()}
                                       </Link>
                                    </td>
                                    {isEditing && !isWhEdit ? (
                                       <td>
                                          <NumberFormat
                                             className="form-control"
                                             value={editCopy.orderPointQty}
                                             decimalScale={3}
                                             fixedDecimalScale
                                             allowNegative={false}
                                             onValueChange={({ floatValue }) => {
                                                handleChange("orderPointQty", floatValue);
                                             }}
                                          />
                                       </td>
                                    ) : (
                                       <td className="data-cell" children={Math.round(datum.orderPointQty).toLocaleString()} />
                                    )}
                                    {isEditing && !isWhEdit ? (
                                       <td>
                                          <NumberFormat
                                             className="form-control"
                                             value={editCopy.fixedOrderQty}
                                             decimalScale={3}
                                             fixedDecimalScale
                                             allowNegative={false}
                                             onValueChange={({ floatValue }) => {
                                                handleChange("fixedOrderQty", floatValue);
                                             }}
                                          />
                                       </td>
                                    ) : (
                                       <td className="data-cell" children={Math.round(datum.fixedOrderQty).toLocaleString()} />
                                    )}

                                    {isEditing ? (
                                       <td className="data-cell">
                                          <Input
                                             type="text"
                                             value={editCopy.bin || ""}
                                             onChange={(e) => handleChange("bin", e.target.value)}
                                             maxLength="20"
                                          />
                                       </td>
                                    ) : (
                                       <td className="data-cell" children={datum.binTrimmed} />
                                    )}

                                    {!showCycle ? null : (
                                       <td>
                                          <NumberFormat
                                             id={index}
                                             name={index}
                                             className="form-control"
                                             value={counts?.qtyList[index]?.cycleCountQty}
                                             // value={findCycleCountQtyById(index)}
                                             decimalScale={3}
                                             fixedDecimalScale
                                             allowNegative={false}
                                             onValueChange={({ floatValue }) => {
                                                onChange(index, floatValue);
                                             }}
                                          />
                                       </td>
                                    )}
                                    {showEdits
                                       ? <td className="data-cell">
                                          <ButtonGroup className="width95px">
                                             <Button
                                                className={`icon-button me-1 ${isEditing ? "" : "float-end"}`}
                                                title={isEditing ? "Save changes" : "Edit line"}
                                                color={isEditing ? "success" : "info"}
                                                onClick={isEditing ? updateItemDesc : () => allowEdit(index)}
                                                disabled={
                                                   (editIndex !== null && readOnly) ||
                                                   // validateEditCopy() ||
                                                   working
                                                }
                                             >
                                                <FontAwesomeIcon className="whiteIcon" icon={isEditing ? "check" : "edit"} />
                                             </Button>
                                             <Button
                                                className={`icon-button ${isEditing ? "" : "vis-hidden"}`}
                                                color="secondary"
                                                title="Discard changes"
                                                onClick={cancelEdit}
                                                disabled={editIndex !== null && readOnly}
                                             >
                                                <FontAwesomeIcon className="whiteIcon" icon="times" />
                                             </Button>
                                          </ButtonGroup>
                                       </td> : null}
                                    {/*based on permissions show the edit column*/}
                                 </tr>
                              );
                           })}
                        </tbody>
                        <tfoot className="table-footer">
                           <tr>
                              <td><strong>TOTALS:</strong></td>
                              <td className={`fw-bold`}>{Math.round(total.totalOnHand).toLocaleString()}</td>
                              <td className={`fw-bold`}>{Math.round(total.totalAllocated).toLocaleString()}</td>
                              <td className={`fw-bold`}>{Math.round(total.totalAvailable).toLocaleString()}</td>
                              <td className={`fw-bold`}>{Math.round(total.totalOnOrder).toLocaleString()}</td>
                              <td colSpan={helpers.hasRole(props.roles, [constants.ROLE_IDS.Admin, constants.ROLE_IDS.PowerUser, constants.ROLE_IDS.EngineeringEdit, constants.ROLE_IDS.WarehouseEdit, constants.ROLE_IDS.InventoryEdit]) ? "5" : "4"}></td>
                           </tr>
                        </tfoot>
                     </Table>
                     :
                     <div>
                        <span>Currenty Inventory was not found for this Item</span>
                     </div>
                  }
               </ServiceThreadSpinner>
            </CardBody>
         </Card>
      </>
   );
}

function AvailableDropDownMenu(props) {
   const [open, setOpen] = useState(false);

   return (
      <td
         className="data-cell"
         onMouseEnter={() => setOpen(true)}
         onMouseLeave={() => setOpen(false)}
         style={{ position: "relative" }}
      >
         <span className="shredLink fw-bold">{props.available}</span>
         <Dropdown
            isOpen={open}
            toggle={() => setOpen(!open)}
            style={{ position: "absolute", top: "1.75em", left: "2em" }}
         >
            <DropdownMenu>
               <DropdownItem className="sort-handle" onClick={() => reportHelpers.getCaseLotInventoryReport(props.itemNum, props.siteId)} >
                  Case Lot Report
               </DropdownItem>
               <DropdownItem className="sort-handle" onClick={() => window.open(`/production-reports`, '_blank')}>
                  Production Reports
               </DropdownItem>
            </DropdownMenu>
         </Dropdown>
      </td >
   );
}
